import { useState, useEffect, useCallback } from "react";

const AUTH_BASE = "https://pulse-auth-api-hkd4a9cgbaa0dtat.swedencentral-01.azurewebsites.net/api";
const BLOB_BASE = "https://pulsedashboarddata.blob.core.windows.net/store-data";

function getToken()           { return sessionStorage.getItem("auth_token"); }
function getAllowedCountries() {
  try { return JSON.parse(sessionStorage.getItem("allowed_countries") || '["SE","DK"]'); }
  catch { return ["SE","DK"]; }
}
function getAllowedStores() {
  try { return JSON.parse(sessionStorage.getItem("allowed_stores") || '[]'); }
  catch { return []; }
}
function setToken(t)  { sessionStorage.setItem("auth_token", t); }
function clearToken() { sessionStorage.removeItem("auth_token"); }

function getLocalDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const ALL_STORES = [
  { code: '26310', country: 'SE' }, { code: '26311', country: 'SE' },
  { code: '26312', country: 'SE' }, { code: '26313', country: 'SE' },
  { code: '26314', country: 'SE' }, { code: '26315', country: 'SE' },
  { code: '26316', country: 'SE' }, { code: '26318', country: 'SE' },
  { code: '26319', country: 'SE' }, { code: '26321', country: 'SE' },
  { code: '26322', country: 'SE' }, { code: '26323', country: 'SE' },
  { code: '26324', country: 'SE' }, { code: '26325', country: 'SE' },
  { code: '26326', country: 'SE' }, { code: '27012', country: 'DK' },
  { code: '27014', country: 'DK' }, { code: '27019', country: 'DK' },
  { code: '27050', country: 'DK' }, { code: '27056', country: 'DK' },
  { code: '27071', country: 'DK' },
];

async function apiLogin(username, password) {
  const res = await fetch(`${AUTH_BASE}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Login failed");
  return json;
}

async function fetchStoreData(country) {
  const allowedStores = getAllowedStores();

  const storesToFetch = ALL_STORES.filter(s =>
    s.country === country &&
    (allowedStores.length === 0 || allowedStores.includes(s.code))
  );

  const results = await Promise.allSettled(
    storesToFetch.map(async ({ code }) => {
      const res = await fetch(`${BLOB_BASE}/store-${code}.json?t=${Date.now()}`);
      if (!res.ok) throw new Error(`Store ${code} not available`);
      const json = await res.json();
      return json.data;
    })
  );

  return results
    .filter(r => r.status === 'fulfilled' && r.value !== null)
    .map(r => r.value);
}

const fmt2 = v => (v == null ? "—" : Number(v).toFixed(2));
const fmt0 = v => (v == null ? "—" : Math.round(Number(v)));

function PctCell({ pct }) {
  const v     = Number(pct);
  const color = v >= 0 ? "#22c55e" : "#ef4444";
  return (
    <td style={{ textAlign: "right", color, fontWeight: 600, padding: "6px 10px", borderBottom: "1px solid #1e1e1e", borderRight: "1px solid #1e1e1e", whiteSpace: "nowrap", fontSize: 12 }}>
      {v >= 0 ? "+" : ""}{fmt2(v)}% {v >= 0 ? "▲" : "▼"}
    </td>
  );
}

function PctBadge({ pct }) {
  const v   = Number(pct);
  const color = v >= 0 ? "#22c55e" : "#ef4444";
  const bg    = v >= 0 ? "#052e16" : "#1c0000";
  return (
    <span style={{ color, background: bg, borderRadius: 4, padding: "2px 6px", fontSize: 10, fontWeight: 700 }}>
      {v >= 0 ? "+" : ""}{fmt2(v)}% {v >= 0 ? "▲" : "▼"}
    </span>
  );
}

function CountrySelector({ country, onChange, isMobile, allowedCountries = ["SE","DK"] }) {
  const countries = [
    { code: "SE", flag: "🇸🇪", label: "Sweden" },
    { code: "DK", flag: "🇩🇰", label: "Denmark" },
  ].filter(c => allowedCountries.includes(c.code));
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {countries.map(c => (
        <button key={c.code} onClick={() => onChange(c.code)}
          style={{
            background: country === c.code ? "#fff" : "transparent",
            border: "1px solid " + (country === c.code ? "#fff" : "rgba(255,255,255,0.3)"),
            color: country === c.code ? "#0a0a0a" : "#fff",
            padding: isMobile ? "4px 8px" : "4px 12px",
            borderRadius: 4,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: isMobile ? 11 : 12,
            fontWeight: country === c.code ? 700 : 400,
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}>
          <span>{c.flag}</span>
          {!isMobile && <span>{c.label}</span>}
        </button>
      ))}
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data = await apiLogin(username, password);
      setToken(data.token);
      sessionStorage.setItem("allowed_countries", JSON.stringify(data.allowedCountries || ["SE","DK"]));
      sessionStorage.setItem("allowed_stores", JSON.stringify(data.allowedStores || []));
      onLogin(data.username);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Mono','Courier New',monospace", padding: 20 }}>
      <div style={{ background: "#141414", border: "1px solid #2a2a2a", borderTop: "3px solid #c80c0c", borderRadius: 8, padding: "40px 36px", width: "100%", maxWidth: 360, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🍕</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", letterSpacing: 2, textTransform: "uppercase" }}>Pulse Dashboard</div>
          <div style={{ fontSize: 11, color: "#555", marginTop: 4, letterSpacing: 2 }}>SIGN IN TO CONTINUE</div>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#666", marginBottom: 6 }}>Username</label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)} autoFocus required
              style={{ width: "100%", background: "#0d0d0d", border: "1px solid #333", borderRadius: 4, padding: "10px 12px", color: "#e5e5e5", fontFamily: "inherit", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#666", marginBottom: 6 }}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
              style={{ width: "100%", background: "#0d0d0d", border: "1px solid #333", borderRadius: 4, padding: "10px 12px", color: "#e5e5e5", fontFamily: "inherit", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
          </div>
          {error && <div style={{ background: "#1c0000", border: "1px solid #c80c0c", borderRadius: 4, padding: "10px 12px", color: "#fca5a5", fontSize: 12, marginBottom: 16 }}>{error}</div>}
          <button type="submit" disabled={loading}
            style={{ width: "100%", background: loading ? "#5a0505" : "#c80c0c", border: "none", borderRadius: 4, padding: "12px", color: "#fff", fontFamily: "inherit", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: loading ? "not-allowed" : "pointer" }}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function StoreCard({ row }) {
  return (
    <div style={{ background: "#141414", border: "1px solid #1e1e1e", borderLeft: "3px solid #c80c0c", borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 16, fontWeight: 700, color: "#fff", letterSpacing: 1 }}>
          Store {row.Store_No}
        </div>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 15, color: "#fff", fontWeight: 600 }}>
          {fmt2(row.CurrentSales)} Kr.
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
        {[
          { label: "Wk %",       value: <PctBadge pct={row.LstWkToCurChangeWkdy} /> },
          { label: "Yr %",       value: <PctBadge pct={row.LstYrToCurChangeWkdy} /> },
          { label: "Orders",     value: fmt0(row.CurrentOrders) },
          { label: "Wk Ord",     value: fmt0(row.LstWkOrdersWkdy) },
          { label: "Yr Ord",     value: fmt0(row.LstYrOrdersWkdy) },
          { label: "AvgTkt",     value: fmt2(row.CurrentAvgTkt) + " Kr." },
          { label: "Wk AvgTkt",  value: fmt2(row.LstWkAvgTktWkdy) + " Kr." },
          { label: "Wk Sales",   value: fmt2(row.LstWkSalesWkdy) + " Kr." },
          { label: "Yr Sales",   value: fmt2(row.LstYrSalesWkdy) + " Kr." },
          { label: "Sal Lbr%",   value: fmt2(row.SalaryLbrPct) + "%" },
          { label: "Hrly Lbr%",  value: fmt2(row.HrlyLbrPct) + "%" },
          { label: "%ATD <25",   value: fmt2(row.LessThan25Day) + "%" },
          { label: "%ATD >45",   value: fmt2(row.GreaterThan45Day) + "%" },
          { label: "%Singles",   value: fmt2(row.SinglesDay) + "%" },
          { label: "Load Time",  value: fmt2(Number(row.LoadTime) / 60), alert: Number(row.LoadTime) / 60 >= 5 },
          { label: "Makeline",   value: fmt0(row.MakeLineQuantity) },
          { label: "Rack Time",  value: fmt2(Number(row.RackTime) / 60), alert: Number(row.RackTime) / 60 >= 15 },
          { label: "Avg Load",   value: fmt2(Number(row.AvgLoadTime) * 1440) },
          { label: "Avg OTD",    value: fmt2(Number(row.AvgOTDTime) * 1440) },
          { label: "Avg Del",    value: fmt2(Number(row.AvgDeliveryTime) * 1440) },
        ].map((m, i) => (
          <div key={i} style={{ background: m.alert ? "#7f1d1d" : "#0d0d0d", borderRadius: 6, padding: "5px 6px", textAlign: "center" }}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 7, color: "#ffffff", letterSpacing: 1, textTransform: "uppercase", marginBottom: 3 }}>{m.label}</div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: m.alert ? "#fca5a5" : "#e5e5e5" }}>{m.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const baseCell = { padding: "6px 10px", borderBottom: "1px solid #1e1e1e", borderRight: "1px solid #1e1e1e", textAlign: "right", whiteSpace: "nowrap", fontSize: 12 };
const ALERT_RED = "#7f1d1d";

const cols = [
  { key: "Store_No",          label: "Store",          render: r => r.Store_No },
  { key: "CurrentSales",      label: "Sales",          render: r => fmt2(r.CurrentSales) + " Kr." },
  { key: "LstWkSalesWkdy",    label: "Wk Sales",       render: r => fmt2(r.LstWkSalesWkdy) + " Kr." },
  { key: "LstWkToCurPct",     label: "Wk %",           special: "pct", field: "LstWkToCurChangeWkdy" },
  { key: "LstYrSalesWkdy",    label: "Yr Sales",       render: r => fmt2(r.LstYrSalesWkdy) + " Kr." },
  { key: "LstYrToCurPct",     label: "Yr %",           special: "pct", field: "LstYrToCurChangeWkdy" },
  { key: "CurrentOrders",     label: "Orders",         render: r => fmt0(r.CurrentOrders) },
  { key: "LstWkOrdersWkdy",   label: "Wk Ord",         render: r => fmt0(r.LstWkOrdersWkdy) },
  { key: "LstYrOrdersWkdy",   label: "Yr Ord",         render: r => fmt0(r.LstYrOrdersWkdy) },
  { key: "CurrentAvgTkt",     label: "AvgTkt",         render: r => fmt2(r.CurrentAvgTkt) + " Kr." },
  { key: "LstWkAvgTktWkdy",   label: "Wk AvgTkt",      render: r => fmt2(r.LstWkAvgTktWkdy) + " Kr." },
  { key: "SalaryLbrPct",      label: "Sal Lbr%",       render: r => fmt2(r.SalaryLbrPct) + "%" },
  { key: "HrlyLbrPct",        label: "Hrly Lbr%",      render: r => fmt2(r.HrlyLbrPct) + "%" },
  { key: "LessThan25Day",     label: "%ATD <25",        render: r => fmt2(r.LessThan25Day) + "%" },
  { key: "GreaterThan45Day",  label: "%ATD >45",        render: r => fmt2(r.GreaterThan45Day) + "%" },
  { key: "SinglesDay",        label: "%Singles",        render: r => fmt2(r.SinglesDay) + "%" },
  { key: "LoadTime",          label: "Load Time",      special: "load" },
  { key: "MakeLineQuantity",  label: "Makeline",       render: r => fmt0(r.MakeLineQuantity) },
  { key: "RackTime",          label: "Rack Time",      special: "rack" },
  { key: "AvgLoadTime",       label: "Avg Load",       render: r => fmt2(Number(r.AvgLoadTime) * 1440) },
  { key: "AvgOTDTime",        label: "Avg OTD",        render: r => fmt2(Number(r.AvgOTDTime) * 1440) },
  { key: "AvgDeliveryTime",   label: "Avg Delivery",   render: r => fmt2(Number(r.AvgDeliveryTime) * 1440) },
];

function DesktopTable({ rows }) {
  return (
    <div style={{ overflowX: "auto", padding: 16 }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900, fontSize: 12 }}>
        <thead>
          <tr>{cols.map(c => <th key={c.key} style={{ ...baseCell, background: "#c80c0c", color: "#fff", fontWeight: 700, textAlign: "center", fontSize: 12, textTransform: "uppercase", position: "sticky", top: 0, zIndex: 1, borderBottom: "3px solid #ff1a1a", letterSpacing: 0.5 }}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => {
            const loadVal = Number(row.LoadTime) / 60;
            const rackVal = Number(row.RackTime) / 60;
            return (
              <tr key={row.Store_No} style={{ background: ri % 2 === 0 ? "#141414" : "#0f0f0f" }}>
                {cols.map(c => {
                  if (c.special === "pct") return <PctCell key={c.key} pct={row[c.field]} />;
                  if (c.special === "load") return (
                    <td key={c.key} style={{ ...baseCell, background: loadVal >= 5 ? ALERT_RED : undefined, color: loadVal >= 5 ? "#fca5a5" : undefined }}>
                      {fmt2(loadVal)}
                    </td>
                  );
                  if (c.special === "rack") return (
                    <td key={c.key} style={{ ...baseCell, background: rackVal >= 15 ? ALERT_RED : undefined, color: rackVal >= 15 ? "#fca5a5" : undefined }}>
                      {fmt2(rackVal)}
                    </td>
                  );
                  return <td key={c.key} style={baseCell}>{c.render(row)}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Dashboard({ username, onLogout }) {
  const isMobile = useIsMobile();
  const allowedCountries = getAllowedCountries();
  const [country, setCountry] = useState(allowedCountries[0] || "SE");
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async (c) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchStoreData(c);
      setRows(data);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(country); }, [country, load]);

  useEffect(() => {
    const id = setInterval(() => load(country), 30000);
    return () => clearInterval(id);
  }, [country, load]);

  const handleCountryChange = (c) => {
    setCountry(c);
    setRows([]);
  };

  const handleLogout = async () => {
    await fetch(`${AUTH_BASE}/logout`, {
      method: "POST",
      headers: { "x-auth-token": getToken() },
    }).catch(() => {});
    clearToken();
    sessionStorage.removeItem("allowed_countries");
    sessionStorage.removeItem("allowed_stores");
    onLogout();
  };

  const countryLabel = country === "SE" ? "🇸🇪 Sweden" : "🇩🇰 Denmark";

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", color: "#e5e5e5", fontFamily: "'DM Mono','Courier New',monospace" }}>
      <style>{`
        :root { --sat: env(safe-area-inset-top, 12px); }
        .dash-header { padding-top: max(12px, env(safe-area-inset-top, 12px)) !important; }
      `}</style>
      <div className="dash-header" style={{ background: "linear-gradient(135deg,#7f0000 0%,#c80c0c 50%,#7f0000 100%)", padding: isMobile ? "12px 14px" : "12px 20px", paddingTop: isMobile ? "var(--sat, 12px)" : "12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "3px solid #ff1a1a", boxShadow: "0 4px 20px rgba(200,12,12,0.4)" }}>
        <div>
          <div style={{ fontSize: 9, letterSpacing: 3, textTransform: "uppercase", opacity: 0.7, marginBottom: 2 }}>Store Performance</div>
          <div style={{ fontSize: isMobile ? 15 : 20, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase" }}>Keys 39 Up</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {allowedCountries.length > 1 && <CountrySelector country={country} onChange={handleCountryChange} isMobile={isMobile} allowedCountries={allowedCountries} />}
          <div style={{ borderLeft: "1px solid rgba(255,255,255,0.2)", paddingLeft: 12, fontSize: 11 }}>
            {!isMobile && <div style={{ opacity: 0.7, marginBottom: 4 }}>Signed in as <strong>{username}</strong></div>}
            <button onClick={handleLogout} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", padding: "3px 10px", borderRadius: 3, cursor: "pointer", fontFamily: "inherit", fontSize: 10, letterSpacing: 1 }}>SIGN OUT</button>
          </div>
        </div>
      </div>
      <div style={{ background: "#111", borderBottom: "1px solid #222", padding: isMobile ? "8px 12px" : "10px 20px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button onClick={() => load(country)}
          style={{ background: "#1a1a1a", border: "1px solid #444", color: "#aaa", padding: "6px 10px", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>↻</button>
        <span style={{ fontSize: 9, color: "#fbbf24" }}>● Live</span>
        {!isMobile && (
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#555" }}>
            {countryLabel} · <strong style={{ color: "#888" }}>{getLocalDate()}</strong> · {rows.length} stores
            {lastRefresh && <span> · Updated: {lastRefresh.toLocaleTimeString()}</span>}
          </span>
        )}
      </div>
      {error && <div style={{ margin: "12px", padding: "10px 14px", background: "#1c0000", border: "1px solid #c80c0c", borderRadius: 6, color: "#fca5a5", fontSize: 12 }}>Error: {error}</div>}
      {loading && <div style={{ textAlign: "center", padding: 40, color: "#c80c0c", fontSize: 13, letterSpacing: 2 }}>Loading {countryLabel} stores...</div>}
      {!loading && rows.length > 0 && isMobile && (
        <div style={{ padding: "10px 10px 24px" }}>
          {rows.map(row => <StoreCard key={row.Store_No} row={row} />)}
        </div>
      )}
      {!loading && rows.length > 0 && !isMobile && <DesktopTable rows={rows} />}
      {!loading && !error && rows.length === 0 && (
        <div style={{ textAlign: "center", padding: 60, color: "#444" }}>No data available.</div>
      )}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  useEffect(() => { if (getToken()) setUser("user"); }, []);
  if (!user) return <LoginScreen onLogin={(u) => setUser(u)} />;
  return <Dashboard username={user} onLogout={() => setUser(null)} />;
}
